import { ApiError } from "@/shared/api/errors";
import { queryKeys } from "@/shared/query/queryClient";
import type { FarmActorScope } from "@/shared/services/types";

/**
 * VISIT-LIVE-01：live 串门的「当前伙伴」与查询缓存键。
 * 标识校验采自 I075-R2（D:/petsoul-i-local-20261007/farm-service-bridge-075-r2 的 src/features/farm/scopeKeys.ts）：
 * 只读自有数据属性（不触发 getter）、不 trim 改值、空串和控制字符一律拒绝；按 I 10-09 定的接口改成 household_id、pet_id 两项都带，
 * 缓存键只带账号、家、宠物（不带 homeId）。当前伙伴只从 HouseholdProvider 选中的那只取，与菜园、小窝同一口径；不猜第一只、不读 cookie。
 */
function isIdentifier(value: unknown): value is string {
  return typeof value === "string" && value !== "" && value.trim() === value && !/[\u0000-\u001f\u007f]/.test(value);
}

/** 只读一个自有数据属性：读两次之间被改、或者用 getter 换值，都不会混进请求。 */
function ownIdentifier(scope: unknown, key: string): string {
  const descriptor = scope && typeof scope === "object" && !Array.isArray(scope) ? Object.getOwnPropertyDescriptor(scope, key) : undefined;
  const value: unknown = descriptor && "value" in descriptor ? descriptor.value : undefined;
  if (isIdentifier(value)) return value;
  throw new ApiError({ kind: "http", status: 400, code: "VALIDATION_FAILED", message: "请先选择当前家庭的宠物。", details: { reason: "farm_actor_scope_required" } });
}

/** live 串门四个方法的查询参数：家和宠物原样带上；缺一项在本地拒绝，不发请求。 */
export function farmActorQuery(scope?: FarmActorScope): { household_id: string; pet_id: string } {
  return { household_id: ownIdentifier(scope, "householdId"), pet_id: ownIdentifier(scope, "petId") };
}

/** 串门页的当前伙伴：账号、家、宠物三样都在才算，少一样就是 null（页面显示「串门暂不可用」）。 */
export type VisitActor = Readonly<{ userId: string; householdId: string; petId: string }>;

type CurrentSelection = {
  userId: string | null;
  household: { household_id: string } | null;
  pet: { pet_id: string } | null;
} | null;

export function visitActorOf(current: CurrentSelection): VisitActor | null {
  const userId = current?.userId, householdId = current?.household?.household_id, petId = current?.pet?.pet_id;
  return isIdentifier(userId) && isIdentifier(householdId) && isIdentifier(petId) ? { userId, householdId, petId } : null;
}

/** 交给 FarmService 的只有家和宠物两项（账号只用来分缓存，不进请求）。 */
export function actorScope(actor: VisitActor): FarmActorScope {
  return { householdId: actor.householdId, petId: actor.petId };
}

/** 串门的查询缓存键：live 按账号＋家＋宠物分开，换一只就是另一份、不串号；演示模式（actor 为 null）照旧用原来的键。 */
export const visitKeys = {
  neighbors: (actor: VisitActor | null) =>
    actor ? (["farm", "neighbors", actor.userId, actor.householdId, actor.petId] as const) : queryKeys.neighbors,
  neighborHome: (actor: VisitActor | null, homeId: string) =>
    actor ? (["farm", "neighbor-home", actor.userId, actor.householdId, actor.petId, homeId] as const) : queryKeys.neighborHome(homeId),
};
